// Tableau 18 — Gestion de stock des intrants (hors PEV). Reports automatiques, liens avec le pré-inventaire et l'inventaire.
import { useMemo, useState } from 'react';
import { STOCK_PROGRAMMES, indexMeManuels, PRE_PROGRAMMES, REFERENCE_PAR_PRODUIT_PALU } from '../../../../shared/business-logic/index.ts';
import type { LigneStock } from '../../../../shared/business-logic/index.ts';
import { useModules } from '../../hooks/useModules.ts';
import { useReferencesPalu } from '../../hooks/useReferencesPalu.ts';
import { EnteteModule, NombreInput, Onglets, PasDePeriode, Message } from '../../components/ui.tsx';
import type { Periode } from '../Accueil.tsx';

const CHAMPS_SAISIE = ['debut', 'recue', 'utilisee', 'redeployee', 'perdue', 'sortieAc', 'sdu', 'joursRupture'] as const;
const vide = (d: any) => CHAMPS_SAISIE.every((k) => d?.[k] === null || d?.[k] === undefined) && !d?.nomPersonnalise;

/** Titre affiché sous la valeur de référence, selon l'intrant (TDR, ACT, artésunate → Tab 8 ; SP → Tab 12). */
const TITRE_REFERENCE: Record<string, string> = {
  tdr: 'Ref Tab 8', artesunateInjectable: 'Ref Tab 8',
  act0_11mois: 'Ref Tab 8', act1_5ans: 'Ref Tab 8', act6_13ans: 'Ref Tab 8', act14ansEtPlus: 'Ref Tab 8',
  sulfadoxinePyrimethamine: 'Ref Tab 12 (TPI)',
};

/** Détail compact « palu grave » par tranche d'âge (sous la référence Artesunate) : petites pastilles, la cellule n'est pas alourdie. */
const TRANCHES_GRAVE: Array<[keyof ReturnType<typeof useReferencesPalu>['artesunateParTranche'], string]> = [
  ['m0_11', '0-11 mois'], ['a1_5', '1-5 ans'], ['a6_13', '6-13 ans'], ['a14_24', '14-24 ans'], ['a25Plus', '25 ans+'],
];
function DetailPaluGrave({ d }: { d: ReturnType<typeof useReferencesPalu>['artesunateParTranche'] }) {
  return (
    <span className="ref-detail" aria-label="Paludisme grave par tranche d'âge">
      <em>Palu grave :</em>
      {TRANCHES_GRAVE.map(([cle, label]) => <span key={cle}>{label} <b>{d[cle]}</b></span>)}
    </span>
  );
}

/** Colonne de référence (Paludisme) : valeur calculée, non modifiable, hors de tout calcul de stock. */
function CelluleReference({ nom, etat }: { nom: string; etat: ReturnType<typeof useReferencesPalu> }) {
  const cleRef = REFERENCE_PAR_PRODUIT_PALU[nom];
  if (!cleRef) return <td className="reference">—</td>;
  const valeur = etat.pret ? etat.references[cleRef] : null;
  return (
    <td className="reference">
      <span className="valeur-ref" aria-label={`${nom} — référence`}>{valeur === null ? '—' : valeur}</span>
      {TITRE_REFERENCE[cleRef] && <small>{TITRE_REFERENCE[cleRef]}</small>}
      {cleRef === 'artesunateInjectable' && etat.pret && <DetailPaluGrave d={etat.artesunateParTranche} />}
    </td>
  );
}

export default function Intrants({ periode }: { periode: Periode | null }) {
  const m = useModules(periode);
  // Valeurs de référence Paludisme : LECTURE SEULE (repère visuel). Jamais passées à calculerModules ni à une formule de stock.
  const refPalu = useReferencesPalu(periode);
  const [prog, setProg] = useState(STOCK_PROGRAMMES[0].code);
  const calc = m.calc;
  const brut = useMemo(() => new Map(m.saisies('STOCK').map((l) => [l.cle, l.donnees as Partial<LigneStock>])), [m.saisies]);
  if (!periode) return <PasDePeriode />;
  if (!m.pret) return <p>Chargement…</p>;
  if (!calc) return <Message niveau="warn">{m.erreur ?? 'Données indisponibles.'}</Message>;

  const parCle = new Map<string, any>(calc.stock.map((l: any) => [l.cle, l]));
  const programme = STOCK_PROGRAMMES.find((p) => p.code === prog)!;

  async function maj(cle: string, champ: (typeof CHAMPS_SAISIE)[number], v: number | null) {
    const suite = { ...(brut.get(cle) ?? {}), [champ]: v };
    if (vide(suite)) await m.supprimer('STOCK', cle); else await m.ecrire('STOCK', cle, suite);
  }
  async function ajouter() {
    const nom = (prompt(`Nom du nouvel intrant (${programme.nom}) :`) ?? '').trim();
    if (nom.length < 2) return;
    const cle = `${prog}:c:${Math.random().toString(36).slice(2, 10)}`;
    await m.majParametres({ ajouterIntrant: { cle, programme: prog, nom } });
  }
  async function retirer(cle: string, nom: string) {
    if (!confirm(`Retirer « ${nom} » de la liste ? Ses saisies de ce mois seront supprimées.`)) return;
    await m.majParametres({ retirerIntrant: cle });
    await m.supprimer('STOCK', cle); await m.supprimer('PRE_LIGNE', cle);
  }

  // Lignes du programme : produits du catalogue (ME : sans ceux remplacés par un médicament traceur) + produits ajoutés + traceurs.
  const manuels = prog === 'ME' ? new Set(indexMeManuels(m.catalogue)) : null;
  const avecReference = prog === 'PALU';
  const lignes: Array<{ cle: string; nom: string; supprimable?: boolean; traceur?: boolean }> = [
    ...programme.produits.map((nom, i) => ({ cle: `${prog}:${i}`, nom })).filter((_, i) => !manuels || manuels.has(i)),
    ...m.intrantsAjoutes.filter((x) => x.programme === prog).map((x) => ({ cle: x.cle, nom: x.nom, supprimable: true })),
    ...(prog === 'ME' ? m.catalogue.filter((x) => x.traceur).map((x) => ({ cle: `ME:i:${x.id}`, nom: x.nom, traceur: true })) : []),
  ];
  const compte = (code: string) => calc.stock.filter((l: any) => l.cle.startsWith(`${code}:`) && l.debutEffectif + (l.recue ?? 0) + (l.utilisee ?? 0) > 0).length;
  const erreurs = (code: string) => calc.stock.filter((l: any) => l.cle.startsWith(`${code}:`) && l.controle?.niveau === 'bad').length;

  return (
    <div>
      <EnteteModule titre="Gestion de stock des intrants (hors PEV)" periode={periode} statut={periode.statut} lectureSeule={m.lectureSeule}
        aide={<>Le <b>stock début</b> reprend automatiquement le <b>stock de fin</b> du mois précédent. Le <b>stock de fin</b> se remplit tout seul (restant fin, ou pré-inventaire 🔗) : tapez une valeur pour la remplacer.</>} />
      <Onglets actif={prog} onChoix={setProg} onglets={STOCK_PROGRAMMES.map((p) => ({ id: p.code, label: p.nom, couleur: p.couleur, compteur: `${compte(p.code)}`, erreurs: erreurs(p.code) }))} />
      <div className="defil">
        <table className="saisie" style={{ ['--c' as any]: programme.couleur }}>
          <thead><tr><th className="fixe">Désignation</th><th>Stock début (1)</th><th>Reçue (2)</th><th>Utilisée (3)</th>{avecReference && <th className="reference">Référence</th>}<th>Redéployée (4)</th><th>Perdue (5)</th><th>Sortie AC (6)</th><th>Restant fin (7)</th><th>Stock de fin SDU (8)</th><th>Jours rupture</th></tr></thead>
          <tbody>
            {lignes.map((l) => {
              const r = parCle.get(l.cle);
              const lie = !!r?.alimenteParInventaire; // ligne alimentée par l'inventaire (médicament traceur)
              const c = r?.controle;
              const saisi = brut.get(l.cle) ?? {};
              const bad = (k: string) => c?.niveau === 'bad' && c.champs.includes(k);
              return (
                <tr key={l.cle}>
                  <td className="fixe">
                    {l.nom}{lie && <span className="lien" title="Début, reçue, utilisée, perdue et stock de fin viennent de l'Inventaire (saisie unique)"> 🔗 inventaire</span>}
                    {l.supprimable && <button className="x" title="Retirer cet intrant" onClick={() => retirer(l.cle, l.nom)}>✕</button>}
                    {c && <small className={c.niveau}>{c.message}</small>}
                    {r?.preInventaire && r.preInventaire.ecart !== null && r.preInventaire.ecart !== 0 && <small className="warn">Pré-inventaire : compté {r.preInventaire.compte} ≠ théorique {r.preInventaire.theorique}</small>}
                  </td>
                  <td><NombreInput label={`${l.nom} — stock début`} classe={r?.debutReporte ? 'reporte' : ''} titre={r?.debutReporte ? 'Reporté du stock de fin du mois précédent' : undefined}
                    valeur={lie ? r.debut : (saisi.debut ?? (r?.debutReporte ? r.debutEffectif : null))} disabled={m.lectureSeule || lie} onChange={(v) => maj(l.cle, 'debut', v)} /></td>
                  <td><NombreInput label={`${l.nom} — reçue`} valeur={lie ? r.recue : saisi.recue} disabled={m.lectureSeule || lie} onChange={(v) => maj(l.cle, 'recue', v)} /></td>
                  <td><NombreInput label={`${l.nom} — utilisée`} classe={bad('utilisee') ? 'bad' : ''} valeur={lie ? r.utilisee : saisi.utilisee} disabled={m.lectureSeule || lie} onChange={(v) => maj(l.cle, 'utilisee', v)} /></td>
                  {avecReference && <CelluleReference nom={l.nom} etat={refPalu} />}
                  <td><NombreInput label={`${l.nom} — redéployée`} classe={bad('redeployee') ? 'bad' : ''} valeur={saisi.redeployee} disabled={m.lectureSeule} onChange={(v) => maj(l.cle, 'redeployee', v)} /></td>
                  <td><NombreInput label={`${l.nom} — perdue`} classe={bad('perdue') ? 'bad' : ''} valeur={lie ? r.perdue : saisi.perdue} disabled={m.lectureSeule || lie} onChange={(v) => maj(l.cle, 'perdue', v)} /></td>
                  <td><NombreInput label={`${l.nom} — sortie AC`} classe={bad('sortieAc') ? 'bad' : ''} valeur={saisi.sortieAc} disabled={m.lectureSeule} onChange={(v) => maj(l.cle, 'sortieAc', v)} /></td>
                  <td className="calcule">{r ? r.restantFin : 0}</td>
                  <td><NombreInput label={`${l.nom} — stock de fin SDU`} classe={`${r?.sduDepuisPreInventaire ? 'lie' : ''} ${r?.sduAutomatique ? 'reporte' : ''} ${bad('sdu') ? 'bad' : ''}`}
                    titre={r?.sduDepuisPreInventaire ? 'Repris du pré-inventaire (détail + gros) — tapez pour remplacer' : r?.sduAutomatique ? 'Calculé : restant fin (7). Il devient automatiquement le stock début du mois suivant — tapez une valeur pour la remplacer' : undefined}
                    valeur={lie ? r.sdu : (saisi.sdu ?? r?.sdu ?? null)} disabled={m.lectureSeule || lie} onChange={(v) => maj(l.cle, 'sdu', v)} /></td>
                  <td><NombreInput label={`${l.nom} — jours de rupture`} classe={bad('joursRupture') ? 'bad' : c?.champs?.includes('joursRupture') ? 'warn' : ''} valeur={saisi.joursRupture} disabled={m.lectureSeule} onChange={(v) => maj(l.cle, 'joursRupture', v)} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!m.lectureSeule && prog !== undefined && <p><button className="secondaire" onClick={ajouter}>+ Ajouter un intrant ({programme.nom})</button></p>}
      {PRE_PROGRAMMES.some((p) => p.code === prog) && <p className="note">Le stock de fin (8) se remplit tout seul quand vous comptez ce produit dans le <b>Pré-inventaire</b>. Vos sorties ci-dessus sont déduites du stock affiché au pré-inventaire.</p>}
      {prog === 'ME' && <p className="note">Les médicaments marqués <b>traceur</b> dans la <b>Liste des médicaments</b> (menu Inventaire) apparaissent ici automatiquement, alimentés par l'inventaire.</p>}
    </div>
  );
}
